export const VIDEO_ASSET_MIME_TYPES = ['video/mp4', 'video/webm'] as const;

export type VideoAssetSource = {
  kind: 'asset';
  assetId: string;
  src: string;
  mimeType: string;
  width: number | null;
  height: number | null;
};

export type VideoEmbedSource = {
  kind: 'embed';
  url: string;
};

export type VideoSource = VideoAssetSource | VideoEmbedSource;

export type VideoPoster =
  | { kind: 'library'; assetId: string; src: string }
  | { kind: 'none' }
  | { kind: 'url'; src: string };

export const EMPTY_VIDEO_ASSET_SOURCE: VideoAssetSource = {
  kind: 'asset',
  assetId: '',
  src: '',
  mimeType: '',
  width: null,
  height: null,
};
export const EMPTY_VIDEO_EMBED_SOURCE: VideoEmbedSource = { kind: 'embed', url: '' };
export const NO_VIDEO_POSTER: VideoPoster = { kind: 'none' };

export type VideoProvider = 'vimeo' | 'youtube';

export type ParsedVideoProvider = {
  provider: VideoProvider;
  id: string;
  canonicalUrl: string;
  embedUrl: string;
};

export type NormalizedVideoSource =
  | {
      kind: 'asset';
      assetId: string;
      src: string;
      mimeType: typeof VIDEO_ASSET_MIME_TYPES[number];
      width: number | null;
      height: number | null;
    }
  | (ParsedVideoProvider & { kind: 'embed' });

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_WATCH_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
const YOUTUBE_EMBED_HOSTS = new Set([...YOUTUBE_WATCH_HOSTS, 'youtube-nocookie.com', 'www.youtube-nocookie.com']);
const PROVIDER_POSTER_HOST_SUFFIXES = [
  'youtube.com',
  'youtube-nocookie.com',
  'youtu.be',
  'ytimg.com',
  'vimeo.com',
  'vimeocdn.com',
];
const LAST_C0_CONTROL_CODE_POINT = 0x1f;
const DELETE_CONTROL_CODE_POINT = 0x7f;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function hasUrlControlCharacter(value: string): boolean {
  return value.split('').some((character) => {
    const codePoint = character.charCodeAt(0);
    return codePoint <= LAST_C0_CONTROL_CODE_POINT || codePoint === DELETE_CONTROL_CODE_POINT;
  });
}

function hasUnsafeRawUrlSyntax(value: string): boolean {
  return (
    value === '' ||
    value !== value.trim() ||
    /\s/.test(value) ||
    value.includes('\\') ||
    value.includes('<') ||
    value.includes('>') ||
    hasUrlControlCharacter(value)
  );
}

function hasNoQuery(url: URL): boolean {
  return url.search === '' && url.hash === '';
}

function hasExactlyOneQueryParam(url: URL, name: string): boolean {
  const entries = Array.from(url.searchParams.entries());
  return url.hash === '' && entries.length === 1 && entries[0][0] === name;
}

function youtubeResult(id: string): ParsedVideoProvider {
  return {
    provider: 'youtube',
    id,
    canonicalUrl: `https://www.youtube.com/watch?v=${id}`,
    embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&controls=1&playsinline=1&cc_load_policy=1`,
  };
}

function vimeoResult(id: string): ParsedVideoProvider {
  return {
    provider: 'vimeo',
    id,
    canonicalUrl: `https://vimeo.com/${id}`,
    embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1&controls=1&dnt=1`,
  };
}

/**
 * Parses only one-video YouTube/Vimeo URLs. Nothing from the authored query string is forwarded:
 * the returned URLs are reconstructed from the validated provider id.
 */
export function parseVideoProviderUrl(value: unknown): ParsedVideoProvider | null {
  if (typeof value !== 'string' || hasUnsafeRawUrlSyntax(value)) return null;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' || url.username !== '' || url.password !== '' || url.port !== '') return null;

  const host = url.hostname.toLowerCase();
  if (host === 'youtu.be') {
    if (!hasNoQuery(url)) return null;
    const id = url.pathname.startsWith('/') ? url.pathname.slice(1) : '';
    return YOUTUBE_ID.test(id) ? youtubeResult(id) : null;
  }

  if (YOUTUBE_WATCH_HOSTS.has(host) && url.pathname === '/watch') {
    if (!hasExactlyOneQueryParam(url, 'v')) return null;
    const id = url.searchParams.get('v') ?? '';
    return YOUTUBE_ID.test(id) ? youtubeResult(id) : null;
  }

  if (YOUTUBE_EMBED_HOSTS.has(host)) {
    if (!hasNoQuery(url)) return null;
    const match = /^\/embed\/([A-Za-z0-9_-]{11})$/.exec(url.pathname);
    return match ? youtubeResult(match[1]) : null;
  }

  if ((host === 'vimeo.com' || host === 'www.vimeo.com') && hasNoQuery(url)) {
    const match = /^\/(\d{1,12})$/.exec(url.pathname);
    return match ? vimeoResult(match[1]) : null;
  }

  if (host === 'player.vimeo.com' && hasNoQuery(url)) {
    const match = /^\/video\/(\d{1,12})$/.exec(url.pathname);
    return match ? vimeoResult(match[1]) : null;
  }

  return null;
}

export function isVideoAssetMimeType(value: unknown): value is typeof VIDEO_ASSET_MIME_TYPES[number] {
  return typeof value === 'string' && VIDEO_ASSET_MIME_TYPES.includes(value.toLowerCase() as never);
}

/** Asset videos are served unchanged from same-origin paths or credential-free HTTPS URLs. */
export function isSafeAssetVideoSrc(value: unknown): value is string {
  if (typeof value !== 'string' || hasUnsafeRawUrlSyntax(value)) return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.username === '' && url.password === '';
  } catch {
    return false;
  }
}

function isProviderOwnedPosterHost(host: string): boolean {
  return PROVIDER_POSTER_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}

/** Author posters may be same-origin or HTTPS, but never a provider thumbnail fetched before consent. */
export function isSafeVideoPosterSrc(value: unknown): value is string {
  if (typeof value !== 'string' || hasUnsafeRawUrlSyntax(value)) return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true;

  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.username === '' &&
      url.password === '' &&
      !isProviderOwnedPosterHost(url.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
}

export function normalizeVideoSource(value: unknown): NormalizedVideoSource | null {
  if (!isRecord(value)) return null;

  if (value.kind === 'asset') {
    const assetId = typeof value.assetId === 'string' ? value.assetId.trim() : '';
    const mimeType = typeof value.mimeType === 'string' ? value.mimeType.toLowerCase() : '';
    if (!assetId || !isSafeAssetVideoSrc(value.src) || !isVideoAssetMimeType(mimeType)) return null;
    return {
      kind: 'asset',
      assetId,
      src: value.src,
      mimeType,
      width: isPositiveFiniteNumber(value.width) ? value.width : null,
      height: isPositiveFiniteNumber(value.height) ? value.height : null,
    };
  }

  if (value.kind === 'embed') {
    const parsed = parseVideoProviderUrl(value.url);
    return parsed ? { kind: 'embed', ...parsed } : null;
  }

  return null;
}

export function normalizeVideoPoster(value: unknown): VideoPoster {
  if (!isRecord(value) || value.kind === 'none') return { kind: 'none' };
  if (value.kind === 'library') {
    const assetId = typeof value.assetId === 'string' ? value.assetId.trim() : '';
    if (assetId && isSafeVideoPosterSrc(value.src)) return { kind: 'library', assetId, src: value.src };
    return { kind: 'none' };
  }
  if (value.kind === 'url' && isSafeVideoPosterSrc(value.src)) return { kind: 'url', src: value.src };
  return { kind: 'none' };
}

const GENERIC_ACCESSIBLE_NAMES = new Set(['video', 'play video', 'watch video']);

export function normalizeVideoAccessibleName(value: unknown): string {
  if (typeof value !== 'string') return '';
  const name = value.trim();
  if (name.length < 3 || GENERIC_ACCESSIBLE_NAMES.has(name.toLowerCase())) return '';
  return name;
}
